import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Search,
  X,
  QrCode,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Switch } from "../../../components/ui/switch";
import { useAuth } from "../../../context/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../../components/AdminSettings";
import RichTextEditor from "../../../components/RichTextEditor";

import { API_URL } from "../../../config";
import { exportToPDF, exportToExcel } from "../../../lib/export";
import { SortableHeader } from "./SortableHeader";

export const SessionDataModal = ({ isOpen, onClose, session, data }) => {
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const sortedData = useMemo(() => {
    const filtered = data.filter(item => 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.phone || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key] || "";
      let bVal = b[sortConfig.key] || "";
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, searchTerm]);

  const columns = [
    { key: "ticket_number", header: "Bilhete" },
    { key: "name", header: "Nome" },
    { key: "email", header: "Email" },
    { key: "phone", header: "Telefone" },
    { key: "church", header: "Igreja" },
    { key: "age", header: "Idade" },
    { key: "final_price", header: "Valor Pago" },
    { key: "coupon_code", header: "Cupão" },
    { key: "payment_status", header: "Status" },
  ];

  const exportData = sortedData.map(item => ({
    ...item,
    final_price: item.final_price != null ? `${Number(item.final_price).toFixed(2)}€` : "-",
    coupon_code: item.coupon_code || "-",
    payment_status: item.payment_status === "paid" ? "Pago" : "Pendente"
  }));

  const title = (() => {
    if (!session) return "Sessão";
    const parts = [];
    if (session.name) parts.push(session.name);
    if (session.subtitle) parts.push(session.subtitle);
    if (session.date) parts.push(session.date);
    if (session.time) parts.push(session.time);
    return parts.join(" · ") || "Sessão";
  })();

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">{title} ({sortedData.length} registos)</DialogTitle>
        </DialogHeader>
        
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              placeholder="Pesquisar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-dark pl-11"
            />
          </div>
          <Button onClick={() => exportToPDF(exportData, columns, title, title)} className="btn-secondary">
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button onClick={() => exportToExcel(exportData, title)} className="btn-secondary">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
        </div>

        <div className="overflow-auto max-h-[60vh]">
          <table className="table-dark w-full">
            <thead className="sticky top-0 bg-zinc-900">
              <tr>
                <SortableHeader label="Bilhete" sortKey="ticket_number" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Nome" sortKey="name" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Email" sortKey="email" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Telefone" sortKey="phone" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Igreja" sortKey="church" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Idade" sortKey="age" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Valor Pago" sortKey="final_price" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Cupão" sortKey="coupon_code" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Status" sortKey="payment_status" currentSort={sortConfig} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item) => (
                <tr key={item.id}>
                  <td className="font-mono text-blue-500">{item.ticket_number}</td>
                  <td className="text-white">{item.name}</td>
                  <td className="text-zinc-400">{item.email}</td>
                  <td className="text-zinc-400">{item.phone}</td>
                  <td className="text-zinc-400">{item.church}</td>
                  <td className="text-zinc-400">{item.age}</td>
                  <td className="text-white font-medium">{item.final_price != null ? `${Number(item.final_price).toFixed(2)}€` : "-"}</td>
                  <td>
                    {item.coupon_code ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 font-mono">{item.coupon_code}</span>
                    ) : (
                      <span className="text-zinc-600">-</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${item.payment_status === "paid" ? "badge-success" : "badge-warning"}`}>
                      {item.payment_status === "paid" ? "Pago" : "Pendente"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Dashboard Overview
